import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Card, Typography, Tabs, Modal, Alert } from "antd";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, db } from "@/config/firebase";
import { useAuthStore } from "@/store/authStore";
import { doc, setDoc, getDoc, Timestamp } from "firebase/firestore";
import { logError } from "@/utils/logger";
import { MaterialIcon } from "@/components/MaterialIcon";
import { BRANDING } from "@/constants/branding";
import { getErrorMessage } from "@/utils/errorMessages";
import { newUserPlanV3 } from "@/utils/newUser";

const { Title, Text } = Typography;

/**
 * Login and registration page
 * Phase 1: Basic email/password authentication
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [loginForm] = Form.useForm();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Get the original destination from location state (set by ProtectedRoute)
  const from = (location.state as { from?: Location })?.from;
  const redirectPath = from ? `${from.pathname}${from.search}` : "/";

  // Redirect to the original destination (or home) once the user is
  // logged in. Subscribe to the shared zustand auth store rather than
  // registering a fresh `onAuthStateChanged` listener: Firebase fires
  // that listener synchronously with the *cached* state on subscribe,
  // which used to bounce the user straight back to the dashboard right
  // after logout (since the cached state was still "logged in" at the
  // moment LoginPage mounted, before signOut had propagated).
  //
  // We only navigate on a real transition to a logged-in user that
  // happens *after* the page has mounted — i.e. handleLogin completes.
  // If you reach /login while already logged in, you can use the app
  // normally; navigation will happen the moment you sign in again.
  const storeUser = useAuthStore((s) => s.user);
  const sawLoggedOutRef = useRef(false);
  useEffect(() => {
    if (!storeUser) {
      sawLoggedOutRef.current = true;
      return;
    }
    // Only redirect once we've observed a logged-out state at least once
    // since mounting (covers post-logout and after-login cases) — never
    // on the initial cached "still logged in" callback.
    if (sawLoggedOutRef.current) {
      navigate(redirectPath, { replace: true });
    }
  }, [storeUser, navigate, redirectPath]);

  // Handle login
  // Note: Navigation is handled by the onAuthStateChanged useEffect above
  // This ensures we navigate only after Firebase has propagated the auth state
  const handleLogin = async (values: { email: string; password: string }) => {
    setLoading(true);
    setLoginError(null);
    try {
      await signInWithEmailAndPassword(auth, values.email, values.password);
      // Don't navigate here - let onAuthStateChanged handle it to avoid race condition
    } catch (error: any) {
      logError("Login failed", error);
      setLoginError(getErrorMessage(error, "Login failed. Please try again."));
      setLoading(false);
    }
    // Don't setLoading(false) on success - keep loading state until navigation happens
  };

  // Handle Google login
  // Note: Navigation is handled by the onAuthStateChanged useEffect above
  const handleGoogleLogin = async () => {
    setLoading(true);
    setLoginError(null);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user document exists, if not create it
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // New user - create Firestore document
        await setDoc(userDocRef, {
          userId: user.uid,
          email: user.email,
          name: user.displayName || user.email?.split("@")[0] || "User",
          createdAt: Timestamp.now(),
          role: "photographer",
          authProvider: "google",
          planV3: newUserPlanV3(),
        });
      }

      // Don't navigate here - let onAuthStateChanged handle it to avoid race condition
    } catch (error: any) {
      const msg = getErrorMessage(error, "Google login failed. Please try again.");
      if (msg) {
        logError("Google login failed", error);
        setLoginError(msg);
      }
      setLoading(false);
    }
    // Don't setLoading(false) on success - keep loading state until navigation happens
  };

  // Handle forgot password
  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setResetMessage({ type: "error", text: "Please enter your email" });
      return;
    }

    setResetLoading(true);
    setResetMessage(null);
    try {
      await sendPasswordResetEmail(auth, resetEmail, {
        url: "https://dashboard.masterlapse.com/login",
        handleCodeInApp: false,
      });
      // Security: Always show same message regardless of whether email exists
      setResetMessage({
        type: "success",
        text: "If an account exists with this email, you will receive a password reset link.",
      });
    } catch (error: any) {
      // Don't reveal if email exists or not for security
      if (error.code === "auth/invalid-email") {
        setResetMessage({ type: "error", text: "Please enter a valid email address" });
      } else {
        setResetMessage({
          type: "success",
          text: "If an account exists with this email, you will receive a password reset link.",
        });
      }
      logError("Password reset error", error);
    } finally {
      setResetLoading(false);
    }
  };

  // Open forgot password modal with pre-filled email if available
  const openForgotPassword = () => {
    const emailFromForm = loginForm.getFieldValue("email");
    if (emailFromForm) {
      setResetEmail(emailFromForm);
    }
    setForgotPasswordOpen(true);
  };

  // Handle registration
  // Note: Navigation is handled by the onAuthStateChanged useEffect above
  const handleRegister = async (values: {
    email: string;
    password: string;
  }) => {
    setLoading(true);
    setRegisterError(null);
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        values.email,
        values.password
      );

      // Create user document in Firestore
      await setDoc(doc(db, "users", userCredential.user.uid), {
        userId: userCredential.user.uid,
        email: values.email,
        name: values.email.split("@")[0],
        createdAt: Timestamp.now(),
        role: "photographer",
        planV3: newUserPlanV3(),
      });

      // Don't navigate here - let onAuthStateChanged handle it to avoid race condition
    } catch (error: any) {
      logError("Registration failed", error);
      setRegisterError(getErrorMessage(error, "Registration failed. Please try again."));
      setLoading(false);
    }
    // Don't setLoading(false) on success - keep loading state until navigation happens
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <Card className="w-full max-w-md">
        <div className="text-center mb-6">
          <Title level={2}>{BRANDING.APP_TITLE}</Title>
          <Text type="secondary">Professional timelapse management</Text>
        </div>

        {/* Google Sign-in Button */}
        <Button
          onClick={handleGoogleLogin}
          loading={loading}
          size="large"
          block
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            backgroundColor: "#fff",
            borderColor: "#dadce0",
            color: "#3c4043",
            fontWeight: 500,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
          </svg>
          Sign in with Google
        </Button>

        {/* Divider */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            margin: "16px 0",
            color: "#8c8c8c",
          }}
        >
          <div style={{ flex: 1, borderBottom: "1px solid #d9d9d9" }} />
          <span style={{ padding: "0 16px" }}>or</span>
          <div style={{ flex: 1, borderBottom: "1px solid #d9d9d9" }} />
        </div>

        <Tabs
          defaultActiveKey="login"
          onChange={() => { setLoginError(null); setRegisterError(null); }}
          items={[
            {
              key: "login",
              label: "Login",
              children: (
                <Form form={loginForm} onFinish={handleLogin} layout="vertical">
                  {loginError && (
                    <Alert
                      message={loginError}
                      type="error"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input
                      prefix={<MaterialIcon name="mail" />}
                      placeholder="Email"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    rules={[
                      { required: true, message: "Please enter your password" },
                      { min: 6, message: "Password must be at least 6 characters" },
                    ]}
                  >
                    <Input.Password
                      prefix={<MaterialIcon name="lock" />}
                      placeholder="Password"
                      size="large"
                    />
                  </Form.Item>

                  <div style={{ textAlign: "right", marginTop: -16, marginBottom: 16 }}>
                    <Button
                      type="link"
                      onClick={openForgotPassword}
                      style={{ padding: 0 }}
                    >
                      Forgot password?
                    </Button>
                  </div>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      block
                      loading={loading}
                    >
                      Login
                    </Button>
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: "register",
              label: "Register",
              children: (
                <Form onFinish={handleRegister} layout="vertical">
                  {registerError && (
                    <Alert
                      message={registerError}
                      type="error"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input
                      prefix={<MaterialIcon name="mail" />}
                      placeholder="Email"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    rules={[
                      { required: true, message: "Please enter a password" },
                      { min: 6, message: "Password must be at least 6 characters" },
                    ]}
                  >
                    <Input.Password
                      prefix={<MaterialIcon name="lock" />}
                      placeholder="Password"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      block
                      loading={loading}
                    >
                      Register
                    </Button>
                  </Form.Item>
                </Form>
              ),
            },
          ]}
        />

        {/* Forgot Password Modal */}
        <Modal
          title="Reset Password"
          open={forgotPasswordOpen}
          onCancel={() => {
            setForgotPasswordOpen(false);
            setResetEmail("");
            setResetMessage(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setForgotPasswordOpen(false);
                setResetEmail("");
                setResetMessage(null);
              }}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={resetLoading}
              onClick={handleForgotPassword}
            >
              Send Reset Link
            </Button>,
          ]}
        >
          <p style={{ marginBottom: 16 }}>
            Enter your email address and we'll send you a link to reset your
            password.
          </p>
          {resetMessage && (
            <Alert
              message={resetMessage.text}
              type={resetMessage.type}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Input
            prefix={<MaterialIcon name="mail" />}
            placeholder="Email"
            size="large"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            onPressEnter={handleForgotPassword}
          />
        </Modal>
      </Card>
    </div>
  );
}
